import React, { useEffect } from 'react';
import * as motion from 'motion/react-m';
import { AnimatePresence } from 'motion/react';
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';
import { AppNotification } from '../../types';

interface ToastProps {
    notifications: AppNotification[];
    onDismiss: (id: string) => void;
}

export const Toast: React.FC<ToastProps> = ({ notifications, onDismiss }) => {
    return (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-3 w-full max-w-md px-4 pointer-events-none">
            <AnimatePresence mode="popLayout">
                {notifications.map((n) => (
                    <ToastItem key={n.id} notification={n} onDismiss={onDismiss} />
                ))}
            </AnimatePresence>
        </div>
    );
};

const ToastItem: React.FC<{ notification: AppNotification; onDismiss: (id: string) => void }> = ({ notification, onDismiss }) => {
    const { id, type, message, submessage, duration = 5000 } = notification;

    useEffect(() => {
        const timer = setTimeout(() => {
            onDismiss(id);
        }, duration);
        return () => clearTimeout(timer);
    }, [id, duration, onDismiss]);

    const backgrounds = {
        success: 'bg-emerald-500 border-emerald-600',
        error: 'bg-rose-500 border-rose-600',
        warning: 'bg-amber-500 border-amber-600',
        info: 'bg-blue-600 border-blue-700'
    };

    const textColors = {
        success: 'text-white',
        error: 'text-white',
        warning: 'text-white',
        info: 'text-white'
    };
    
    const subtextColors = {
        success: 'text-emerald-50',
        error: 'text-rose-50',
        warning: 'text-amber-50',
        info: 'text-blue-50'
    };

    const iconColors = {
        success: 'text-white',
        error: 'text-white',
        warning: 'text-white',
        info: 'text-white'
    };

    const icons = {
        success: <CheckCircle2 className={`w-5 h-5 ${iconColors.success}`} />,
        error: <AlertCircle className={`w-5 h-5 ${iconColors.error}`} />,
        warning: <AlertTriangle className={`w-5 h-5 ${iconColors.warning}`} />,
        info: <Info className={`w-5 h-5 ${iconColors.info}`} />
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
            className={`pointer-events-auto w-full flex items-start gap-4 p-4 rounded-3xl border shadow-2xl ${backgrounds[type]}`}
        >
            <div className="mt-0.5">{icons[type]}</div>
            <div className="flex-1 min-w-0">
                <h4 className={`text-sm font-black uppercase tracking-wider ${textColors[type]}`}>{message}</h4>
                {submessage && <p className={`text-xs mt-1 font-medium leading-relaxed ${subtextColors[type]}`}>{submessage}</p>}
            </div>
            <button 
                onClick={() => onDismiss(id)}
                className="p-1 hover:bg-black/10 rounded-full transition-colors fill-white/80"
            >
                <X className={`w-4 h-4 ${textColors[type]}`} />
            </button>
        </motion.div>
    );
};
